import { useEffect, useState } from "react";
import moment from "moment/moment";

import { useTranslation } from "react-i18next";
import { createCommercialDates, getAllChannels } from "../../../api/Services";
import { useNavigate } from "react-router-dom";

import { Form, message, Button, Input, Select, Spin, DatePicker } from "antd";
import { LoadingOutlined } from "@ant-design/icons";

import styles from "./NewCommercialDate.module.scss";

const NewCommercialDates = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [channelOptions, setChannelOptions] = useState<
    { value: number; label: string }[]
  >([]);

  const [form] = Form.useForm();

  useEffect(() => {
    getAllChannels().then((channels: any[]) => {
      setChannelOptions(
        (channels ?? []).map((c) => ({
          value: c.id,
          label:
            c?.type?.descriptionEs || c?.type?.type || `${t("Common-Channels")} ${c.id}`,
        }))
      );
    });
  }, [t]);

  const handleCreate = async () => {
    await form.validateFields().then(async (values) => {
      setLoading(true);
      const { name, channelIds } = values;
      const startingDate = moment(values.startingDate, "YYYY-MM-DD").format(
        "YYYY-MM-DD"
      );
      const endingDate = moment(values.endingDate, "YYYY-MM-DD").format(
        "YYYY-MM-DD"
      );
      let response = await createCommercialDates(
        name,
        startingDate,
        endingDate,
        channelIds
      );
      if (response) {
        message.success(t("Common-CreateSuccessMessage"));
        form.resetFields();
        navigate("/admin/parameters");
      } else message.error(t("Common-ErrorMessage"));

      setLoading(false);
    });
  };

  return loading ? (
    <Spin
      indicator={<LoadingOutlined className="ol-loading-spin-icon" />}
      delay={500}
    />
  ) : (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t("Common-NewCommercialDate")}</h1>
        <Button
          onClick={handleCreate}
          className={styles.saveButton}
          type="primary"
        >
          {t("Common-Add")}
        </Button>
      </div>
      <div className={styles.contentWrapper}>
        <div className={styles.newWrapper}>
          <Form
            name="newCommercialDate"
            layout="vertical"
            initialValues={{ remember: true }}
            form={form}
            autoComplete="off"
            className={styles.formNew}
          >
            <Form.Item
              className={styles.newItem}
              label={t("Common-Name")}
              name="name"
              rules={[
                {
                  required: true,
                  message: t("Common-NameNotEntered"),
                },
              ]}
            >
              <Input placeholder={t("Common-Name")} />
            </Form.Item>
            <Form.Item
              className={`${styles.newItem} ${styles.customDatePicker}`}
              label={t("Common-StartDate")}
              name="startingDate"
              rules={[
                {
                  required: true,
                  message: t("Common-StartDate"),
                },
              ]}
            >
              <DatePicker
                className={styles.dateSelector}
                showTime={false}
                getPopupContainer={(triggerNode) =>
                  triggerNode.parentNode as HTMLElement
                }
                format="MM-DD"
              />
            </Form.Item>
            <Form.Item
              className={`${styles.newItem} ${styles.customDatePicker}`}
              label={t("Common-EndDate")}
              name="endingDate"
              rules={[
                {
                  required: true,
                  message: t("Common-EndDate"),
                },
              ]}
            >
              <DatePicker
                className={styles.dateSelector}
                showTime={false}
                getPopupContainer={(triggerNode) =>
                    triggerNode.parentNode as HTMLElement
                }
                format="MM-DD"
              />
            </Form.Item>
            <Form.Item
              className={styles.newItem}
              label={t("Common-Channels")}
              name="channelIds"
              tooltip={t("CommercialDate-ChannelsHint")}
            >
              <Select
                mode="multiple"
                allowClear
                placeholder={t("CommercialDate-ChannelsPlaceholder")}
                options={channelOptions}
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
              />
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default NewCommercialDates;
